import React from 'react';
import {FormProvider, useForm, useFormContext} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';

import {
  LoadingButton,
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
  NewSelectField,
  NewTextInputField,
} from '@edna/components';
import {useOnChange} from '@edna/hooks';

import {LeftButton} from 'src/components/primitives';
import {ISO3CountryCodeList} from 'src/utils';
import {joiResolver} from 'src/utils/validator';

import {useGetPaymentDetailsQuery, useSavePaymentDetailsMutation} from '../api';
import {EResidentCountry} from '../definitions';
import {getActionName, getResidentCountry} from '../utils';

type TProps = {
  closeModal: TEmptyFunction;
};

export const VALIDATION_SCHEMAS: Record<EResidentCountry, TCreateValidateSchema> = {
  [EResidentCountry.FOREIGN]: (joi) =>
    joi.object({
      country: joi.string().required(),
      bankName: joi.string().required(),
      corrBankAccount: joi.string().required(),
      operBankAccount: joi.string().required(),
      bic: joi.string().required(),
      bankAddress: joi.string().required(),
      iban: joi.string().required(),
    }),

  [EResidentCountry.RUS]: (joi) =>
    joi.object({
      country: joi.string().required(),
      bankName: joi.string().required(),
      corrBankAccount: joi.string().required(),
      operBankAccount: joi.string().required(),
      bic: joi.string().required(),
    }),
  [EResidentCountry.BLR]: (joi) =>
    joi.object({
      country: joi.string().required(),
      bankName: joi.string().required(),
      bankAddress: joi.string(),
      bic: joi
        .string()
        .custom((value, helpers) => {
          if (value.length !== 8 && value.length !== 11) {
            return helpers.error('invalidBIC');
          }

          if (!/^[a-z]{4}by\d[a-z0-9]\d{0,3}$/i.test(value)) {
            return helpers.error('invalidBIC');
          }

          const [values] = helpers.state.ancestors;

          if (values.iban?.length > 0 && value.slice(0, 4) !== values.iban.slice(4, 8)) {
            return helpers.error('invalidBIC');
          }

          return value;
        })
        .required(),
      iban: joi
        .string()
        .max(28)
        .custom((value, helpers) => {
          if (!value.startsWith('BY')) {
            return helpers.error('invalidIBAN');
          }

          const [values] = helpers.state.ancestors;

          if (values.bic?.length > 0 && value.slice(4, 8) !== values.bic.slice(0, 4)) {
            return helpers.error('invalidIBAN');
          }

          return value;
        }),
    }),
};

const ForeignPaymentDetails = React.memo(() => {
  const {t} = useTranslation();

  return (
    <>
      <NewTextInputField name="bankName" label={t('CompanyDetails:bankName')} />
      <NewTextInputField name="corrBankAccount" label={t('CompanyDetails:corrBankAccount')} />
      <NewTextInputField name="bankAddress" label={t('CompanyDetails:bankAddress')} />
      <NewTextInputField name="operBankAccount" label={t('CompanyDetails:operBankAccount')} />
      <NewTextInputField name="iban" label={t('CompanyDetails:iban')} />
      <NewTextInputField name="bic" label={t('CompanyDetails:bic')} />
    </>
  );
});

ForeignPaymentDetails.displayName = 'ForeignPaymentDetails';

const RusPaymentDetails = React.memo(() => {
  const {t} = useTranslation();

  return (
    <>
      <NewTextInputField name="bankName" label={t('CompanyDetails:bankName')} />
      <NewTextInputField name="bic" label={t('CompanyDetails:rcbic')} />
      <NewTextInputField name="operBankAccount" label={t('CompanyDetails:operBankAccount')} />
      <NewTextInputField name="corrBankAccount" label={t('CompanyDetails:corrBankAccount')} />
    </>
  );
});

RusPaymentDetails.displayName = 'RusPaymentDetails';

const BlrPaymentDetails = React.memo(() => {
  const {t} = useTranslation();

  const {
    formState: {dirtyFields},
    watch,
    trigger,
  } = useFormContext();

  const bic = watch('bic');
  const iban = watch('iban');

  React.useEffect(() => {
    if (dirtyFields.iban) {
      trigger('iban');
    }
  }, [bic]);

  React.useEffect(() => {
    if (dirtyFields.bic) {
      trigger('bic');
    }
  }, [iban]);

  return (
    <>
      <NewTextInputField name="bankName" label={t('CompanyDetails:bankName')} />
      <NewTextInputField name="bankAddress" label={t('CompanyDetails:blrBankAddress')} />
      <NewTextInputField name="bic" label={t('CompanyDetails:blrBic')} />
      <NewTextInputField name="iban" label={t('CompanyDetails:blrIban')} />
    </>
  );
});

BlrPaymentDetails.displayName = 'BlrPaymentDetails';

const PaymentDetailsModal = React.memo<TProps>(({closeModal}) => {
  const {t} = useTranslation();
  const {id: supportTenantId} = useParams<'id'>();

  const {data: paymentDetails} = useGetPaymentDetailsQuery(supportTenantId);
  const [savePaymentDetails, {isSuccess}] = useSavePaymentDetailsMutation();

  const resolver = React.useCallback(
    (values, context, options) =>
      joiResolver(VALIDATION_SCHEMAS[getResidentCountry(values.country)])(values, context, options),
    [],
  );

  const methods = useForm({
    mode: 'all',
    defaultValues: paymentDetails,
    resolver,
  });

  const residentCountry = getResidentCountry(methods.watch('country'));

  useOnChange(() => methods.reset(paymentDetails), [paymentDetails]);

  useOnChange(() => {
    if (isSuccess) {
      closeModal();
    }
  }, [isSuccess]);

  const submitPaymentDetailsForm = React.useCallback(
    (values) => savePaymentDetails({supportTenantId, data: values}),
    [supportTenantId, savePaymentDetails],
  );

  const paymentDetailsAction = React.useMemo(() => getActionName(paymentDetails), [paymentDetails]);

  return (
    <Modal width="582px">
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(submitPaymentDetailsForm)}>
          <ModalHeader>
            {t(`CompanyDetails:detailsAction.${paymentDetailsAction}`, {
              detailsName: t('CompanyDetails:modalTitle.paymentDetails'),
            })}
          </ModalHeader>
          <ModalContent>
            <NewSelectField
              isValueCompareInSearch
              name="country"
              label={t('CompanyDetails:country')}
              options={ISO3CountryCodeList.map((code) => ({
                label: t(`Country:iso3.${code}`),
                value: code,
              }))}
            />
            {residentCountry === EResidentCountry.FOREIGN && <ForeignPaymentDetails />}
            {residentCountry === EResidentCountry.RUS && <RusPaymentDetails />}
            {residentCountry === EResidentCountry.BLR && <BlrPaymentDetails />}
            <ModalActions>
              <LeftButton onClick={closeModal}>{t('Common:button.cancel')}</LeftButton>
              <LoadingButton appearance="primary" type="submit" data-selector="Common:button.save">
                {t('Common:button.save')}
              </LoadingButton>
            </ModalActions>
          </ModalContent>
        </form>
      </FormProvider>
    </Modal>
  );
});

PaymentDetailsModal.displayName = 'PaymentDetailsModal';

export default PaymentDetailsModal;
